import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { FattureActions } from './fatture.actions';
import { FatturePort } from '../../core/services/fatture/fatture.port';
import { LoadingService } from '../../core/services/loading/loading.service';
import {catchError, map, of, switchMap, tap, finalize, withLatestFrom, filter, from} from 'rxjs';
import { selectPageSize } from './fatture.selectors';
import { SwalService } from '../../core/services/alerts/swal.service';
import Swal from 'sweetalert2';
import {ImportFattureRequestDto, LottoPassivoSummaryDto} from '../../core/models/fatture.model';
import {
  ImportLottiSdiDialogComponent
} from '../../features/fatture/components/import-lotti-sdi-dialog/import-lotti-sdi-dialog';
import {MatDialog} from '@angular/material/dialog';

@Injectable()
export class FattureEffects {
  private actions$ = inject(Actions);
  private api = inject(FatturePort);
  private store = inject(Store);
  private loading = inject(LoadingService);
  private swalService = inject(SwalService);

  loadList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FattureActions.loadList),
      withLatestFrom(this.store.select(selectPageSize)),
      switchMap(([{ params }, pageSize]) => {
        const queryParams = {
          ...params,
          size: params?.size ?? pageSize,
          page: params?.page ?? 0,
        };
        return this.api.getList(queryParams).pipe(
          map(response =>
            FattureActions.loadListSuccess({
              items: response.content,
              totalElements: response.totalElements,
              totalPages: response.totalPages,
              pageIndex: response.pageIndex,
            })
          ),
          catchError(err =>
            of(FattureActions.loadListFailure({ error: err?.message ?? 'Errore caricamento' }))
          ),
        );
      })
    )
  );

  loadDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FattureActions.loadDetails),
      tap(() => this.loading.show()),
      switchMap(({ id }) =>
        this.api.getById(id).pipe(
          map(item => FattureActions.loadDetailsSuccess({ item })),
          catchError(err =>
            of(FattureActions.loadDetailsFailure({ error: err?.message ?? 'Errore dettagli' }))
          ),
          finalize(() => this.loading.hide()),
        )
      )
    )
  );

  showLoadFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FattureActions.loadListFailure, FattureActions.loadDetailsFailure),
        tap(({ error }) => this.swalService.apiError(error))
      ),
    { dispatch: false }
  );
/*
  loadLottiSdiToImport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FattureActions.loadLottiSdiToImport),
      switchMap(({ year }) =>
        this.api.getLottiSdiToImport(year).pipe(
          map(items => FattureActions.loadLottiSdiToImportSuccess({ items })),
          catchError(err =>
            of(FattureActions.loadLottiSdiToImportFailure({
              error: err?.message ?? 'Errore caricamento fatture SDI'
            }))
          )
        )
      )
    )
  );

  importLottiSdi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FattureActions.importLottiSdi),
      switchMap(({ lotti }) =>
        this.api.importLottiSdi({ lotti }).pipe(
          map(response => FattureActions.importLottiSdiSuccess({ response })),
          catchError(err =>
            of(FattureActions.importLottiSdiFailure({
              error: err?.message ?? 'Errore import fatture SDI'
            }))
          )
        )
      )
    )
  );

  reloadAfterSdiImport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FattureActions.importLottiSdiSuccess),
      map(() => FattureActions.loadList({ params: undefined }))
    )
  );
  */

  setStatoFiscale$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FattureActions.setStatoFiscale),
      switchMap(({ id, statoFiscale }) =>
        this.api.setStatoFiscale(id, statoFiscale).pipe(
          map(() => FattureActions.operationSuccess({
            message: 'Stato fiscale aggiornato',
            reloadId: id,
          })),
          catchError(err => of(FattureActions.operationFailure({
            error: err?.message ?? 'Errore aggiornamento stato fiscale',
          })))
        )
      )
    )
  );

  setAsCostoFisso$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FattureActions.setAsCostoFisso),
      switchMap(({ id }) =>
        this.api.setAsCostoFisso(id).pipe(
          map(() => FattureActions.operationSuccess({
            message: 'Fattura impostata come costo fisso',
            reloadId: id,
          })),
          catchError(err => of(FattureActions.operationFailure({
            error: err?.message ?? 'Errore impostazione costo fisso',
          })))
        )
      )
    )
  );

  associateFornitore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FattureActions.associateFornitore),
      switchMap(({ id, idFornitore }) =>
        this.api.associateFornitore(id, idFornitore).pipe(
          map(() => FattureActions.operationSuccess({
            message: 'Fornitore associato',
            reloadId: id,
          })),
          catchError(err => of(FattureActions.operationFailure({
            error: err?.message ?? 'Errore associazione fornitore',
          })))
        )
      )
    )
  );

  endFornitoreAssociation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FattureActions.endFornitoreAssociation),
      switchMap(({ id }) =>
        this.api.endFornitoreAssociation(id).pipe(
          map(() => FattureActions.operationSuccess({
            message: 'Fornitore dissociato',
            reloadId: id,
          })),
          catchError(err => of(FattureActions.operationFailure({
            error: err?.message ?? 'Errore dissociazione fornitore',
          })))
        )
      )
    )
  );

  associateCantiere$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FattureActions.associateCantiere),
      switchMap(({ id, idCantiere }) =>
        this.api.associateCantiere(id, idCantiere).pipe(
          map(() => FattureActions.operationSuccess({
            message: 'Cantiere associato',
            reloadId: id,
          })),
          catchError(err => of(FattureActions.operationFailure({
            error: err?.message ?? 'Errore associazione cantiere',
          })))
        )
      )
    )
  );

  endCantiereAssociation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FattureActions.endCantiereAssociation),
      switchMap(({ id }) =>
        this.api.endCantiereAssociation(id).pipe(
          map(() => FattureActions.operationSuccess({
            message: 'Cantiere dissociato',
            reloadId: id,
          })),
          catchError(err => of(FattureActions.operationFailure({
            error: err?.message ?? 'Errore dissociazione cantiere',
          })))
        )
      )
    )
  );

  associateArticoloCantiere$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FattureActions.associateArticoloCantiere),
      switchMap(({ idArticolo, idCantiere }) =>
        this.api.associateArticoloCantiere(idArticolo, idCantiere).pipe(
          map(() => FattureActions.operationSuccess({
            message: 'Articolo associato al cantiere',
          })),
          catchError(err => of(FattureActions.operationFailure({
            error: err?.message ?? 'Errore associazione articolo-cantiere',
          })))
        )
      )
    )
  );

  endArticoloCantiereAssociation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FattureActions.endArticoloCantiereAssociation),
      switchMap(({ idArticolo }) =>
        this.api.endArticoloCantiereAssociation(idArticolo).pipe(
          map(() => FattureActions.operationSuccess({
            message: 'Articolo dissociato dal cantiere',
          })),
          catchError(err => of(FattureActions.operationFailure({
            error: err?.message ?? 'Errore dissociazione articolo-cantiere',
          })))
        )
      )
    )
  );

  reloadAfterOperation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FattureActions.operationSuccess),
      filter(({ reloadId }) => !!reloadId),
      map(({ reloadId }) => FattureActions.loadDetails({ id: reloadId! }))
    )
  );

  importXml$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FattureActions.importXml),
      switchMap(({ file }) =>
        this.api.importXml(file).pipe(
          map(() => FattureActions.importXmlSuccess()),
          catchError(err => of(FattureActions.importXmlFailure({
            error: err?.message ?? 'Errore import XML',
          })))
        )
      )
    )
  );

  reloadAfterXmlImport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FattureActions.importXmlSuccess),
      map(() => FattureActions.loadList({ params: undefined }))
    )
  );


  loadLottiSdiToImport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FattureActions.loadLottiSdiToImport),
      switchMap(({ year }) =>
        this.api.getLottiSdiToImport(year).pipe(
          map(items => FattureActions.loadLottiSdiToImportSuccess({ items })),
          catchError(err => of(FattureActions.loadLottiSdiToImportFailure({
            error: err?.message ?? 'Errore caricamento fatture SDI',
          })))
        )
      )
    )
  );
/*
  confirmImportLottiSdi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FattureActions.loadLottiSdiToImportSuccess),
      switchMap(async ({ items }) => {
        if (!items.length) {
          await Swal.fire({
            icon: 'info',
            title: 'Nessuna fattura da importare',
            text: 'Non ci sono fatture SDI da importare per l’anno selezionato.',
          });

          return FattureActions.operationSuccess({
            message: 'Nessuna fattura da importare',
          });
        }

        const result = await Swal.fire({
          icon: 'question',
          title: 'Fatture trovate',
          text: `Sono state trovate ${items.length} fatture da importare. Vuoi importarle ora?`,
          showCancelButton: true,
          confirmButtonText: 'Importa',
          cancelButtonText: 'Annulla',
        });

        if (!result.isConfirmed) {
          return FattureActions.operationSuccess({
            message: 'Import annullato',
          });
        }

        return FattureActions.importLottiSdi({
          lotti: items,
        });
      })
    )
  );
*/
  importLottiSdi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FattureActions.importLottiSdi),
      switchMap(({ lotti }) => {
        const request: ImportFattureRequestDto = {
          lotti: lotti.map(lotto => ({
            idPubblicoLotto: lotto.IdentificativoPubblicoLotto,
            idPubblicoDocumentale: lotto.IdentificativoPubblicoDocumentale,
            dataRicezione: lotto.DataRicezione,
          })),
        };

        return this.api.importLottiSdi(request).pipe(
          map(response => FattureActions.importLottiSdiSuccess({ response })),
          catchError(err =>
            of(FattureActions.importLottiSdiFailure({
              error: err?.message ?? 'Errore import fatture SDI',
            }))
          )
        );
      })
    )
  );

  reloadAfterSdiImport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FattureActions.importLottiSdiSuccess),
      map(() => FattureActions.loadList({ params: undefined }))
    )
  );
  private dialog = inject(MatDialog);
  openSdiDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FattureActions.loadLottiSdiToImportSuccess),
      switchMap(({ items }) => {
        if (!items.length) {
          return from(
            Swal.fire({
              icon: 'info',
              title: 'Nessuna fattura da importare',
              text: 'Non ci sono fatture SDI da importare per l’anno selezionato.',
            })
          ).pipe(
            map(() => FattureActions.operationSuccess({
              message: 'Nessuna fattura da importare',
            }))
          );
        }

        const dialogRef = this.dialog.open(ImportLottiSdiDialogComponent, {
          width: '900px',
          maxWidth: '95vw',
          data: { items },
        });

        return dialogRef.afterClosed().pipe(
          map((selected: LottoPassivoSummaryDto[] | undefined) => {
            if (!selected?.length) {
              return FattureActions.operationSuccess({
                message: 'Import annullato',
              });
            }

            return FattureActions.importLottiSdi({
              lotti: selected,
            });
          })
        );
      })
    )
  );
}
